// src/hooks/useMediaManager.ts
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CarouselApi } from 'ui/Carousel';
import { validateUploadFile } from 'util/uploadValidation';
import {
  DEFAULT_CARD_ASPECT,
  aspectRatioStyle,
  extractIntrinsicDimensions,
  resolveGroupAspect,
  type IntrinsicDimensions,
} from 'util/mediaAspect';

/**
 * Local upload slot. `intrinsic` is populated asynchronously after a file is
 * picked so the upload pipeline can forward width/height/aspectRatio to the
 * server's finalize call without blocking the picker UI on metadata
 * extraction.
 */
export type MediaSlot = {
  file: File;
  preview: string;
  type: 'image' | 'video';
  order: number;
  intrinsic?: IntrinsicDimensions;
};

export function useMediaManager(maxFiles = 20) {
  const [media, setMedia] = useState<MediaSlot[]>([]);
  const [error, setError] = useState<string>('');
  const [isUploading, setIsUploading] = useState(false);
  const [currentIdx, setCurrentIdx] = useState(0);
  const carouselApiRef = useRef<CarouselApi>();

  useEffect(() => {
    if (currentIdx >= media.length && media.length > 0) setCurrentIdx(0);
  }, [media.length, currentIdx]);

  useEffect(() => () => media.forEach(m => URL.revokeObjectURL(m.preview)), [media]);

  const selectFiles = useCallback((files: FileList | null) => {
    if (!files) return;
    const selected = Array.from(files).slice(0, Math.max(0, maxFiles - media.length));
    const valid: MediaSlot[] = [];
    const invalid: string[] = [];

    selected.forEach((file) => {
      const validation = validateUploadFile(file, 'media-manager');
      if (!validation.valid || !validation.category) {
        invalid.push(`${file.name} (${validation.message})`);
        return;
      }
      const preview = URL.createObjectURL(file);
      const type = validation.category;
      valid.push({ file, preview, type, order: media.length + valid.length + 1 });
    });

    if (invalid.length) setError(`Invalid files rejected: ${invalid.join(', ')}`);
    else setError('');

    if (valid.length) {
      setMedia(prev => [...prev, ...valid]);
      // Resolve intrinsic dimensions in the background. Each promise either
      // updates its slot's `intrinsic` field or no-ops on failure; the
      // upload pipeline tolerates missing data.
      valid.forEach((slot) => {
        void extractIntrinsicDimensions(slot.file).then((dims) => {
          if (!dims) return;
          setMedia((prev) => prev.map((entry) =>
            entry.file === slot.file ? { ...entry, intrinsic: dims } : entry,
          ));
        });
      });
    }
  }, [media.length, maxFiles]);

  const removeAt = useCallback((index: number) => {
    setMedia(prev => {
      const toRemove = prev[index];
      URL.revokeObjectURL(toRemove.preview);
      const next = prev.filter((_, i) => i !== index);
      return next.map((f, i) => ({ ...f, order: i + 1 }));
    });
  }, []);

  const move = useCallback((index: number, dir: 'left' | 'right') => {
    setMedia(prev => {
      const arr = [...prev];
      let newIndex = index;
      if (dir === 'left' && index > 0) {
        [arr[index], arr[index - 1]] = [arr[index - 1], arr[index]];
        newIndex = index - 1;
      } else if (dir === 'right' && index < arr.length - 1) {
        [arr[index], arr[index + 1]] = [arr[index + 1], arr[index]];
        newIndex = index + 1;
      }
      setTimeout(() => carouselApiRef.current?.scrollTo(newIndex), 0);
      return arr.map((f, i) => ({ ...f, order: i + 1 }));
    });
  }, []);

  /**
   * Resolved aspect ratio for the current set of slots.
   * Uses intrinsic measurements when available; otherwise falls back to a
   * neutral 4:5 portrait. Always clamped between 3:4 and 16:9 so the picker
   * preview never explodes to fill the viewport.
   */
  const resolvedAspect = useMemo<number>(() => {
    if (media.length === 0) return DEFAULT_CARD_ASPECT;
    const ratios = media.map((slot) => slot.intrinsic?.aspectRatio);
    return resolveGroupAspect(ratios, DEFAULT_CARD_ASPECT);
  }, [media]);

  /**
   * Inline style consumers can spread onto the carousel's bounding frame.
   * Preferred over `aspectClass()` for new code.
   */
  const aspectStyle = useCallback(
    () => aspectRatioStyle(resolvedAspect),
    [resolvedAspect],
  );

  /**
   * Legacy class-based aspect helper kept for backwards compatibility with
   * `MediaManagerCarousel` consumers that take a className string.
   *
   * Tailwind's JIT only generates classes that appear as literal strings in
   * source files, so this helper picks the closest match from a small fixed
   * set of arbitrary-value classes that are statically referenced below.
   */
  const aspectClass = useCallback(() => {
    if (resolvedAspect <= 0.78) return "aspect-[3/4]";
    if (resolvedAspect <= 0.9) return "aspect-[4/5]";
    if (resolvedAspect <= 1.05) return "aspect-[1/1]";
    if (resolvedAspect <= 1.4) return "aspect-[5/4]";
    return "aspect-[16/9]";
  }, [resolvedAspect]);

  const clear = useCallback(() => {
    setMedia(prev => {
      prev.forEach((slot) => URL.revokeObjectURL(slot.preview));
      return [];
    });
    setError('');
    setIsUploading(false);
    setCurrentIdx(0);
  }, []);

  const orders = () => media.map(m => m.order);

  return {
    media, error, isUploading, setIsUploading,
    currentIdx, setCurrentIdx, carouselApiRef,
    selectFiles, removeAt, move,
    aspectClass, aspectStyle, resolvedAspect,
    orders, clear,
  };
}
